import { createApiRef, OAuthApi } from '@backstage/core-plugin-api';
import { Octokit } from '@octokit/rest';
import { GITHUB_ORG, GITHUB_REPO } from './AIProvisioning.service';

export const AZURE_POLICY_FILE_PATH = 'azure/policies/tags/EnforceAAAppShortName/policy.json';

export interface AzureAppShortnamePolicy {
  properties?: {
    parameters?: {
      tagValue?: {
        defaultValue?: string[];
      };
    };
  };
}

export interface AzureOnboardedApps {
  onboardedAppShortNames: string[];
}

export const validateAzureOnboarding = (policy: AzureOnboardedApps, appShortname: string): boolean => {
  return policy.onboardedAppShortNames.includes(appShortname);
};

export interface AIAzureOnboardingServiceApi {
  fetchAzureOnboardingPolicy(): Promise<AzureOnboardedApps>;
}

export const AIAzureOnboardingServiceApiRef = createApiRef<AIAzureOnboardingServiceApi>({
  id: 'plugin.devkit.ai-azure-onboarding.service',
});

export class AIAzureOnboardingService implements AIAzureOnboardingServiceApi {
  constructor(
    private readonly ghecApiUrl: string,
    private readonly ghecAuthApi: OAuthApi,
  ) {}

  private async getOctokit(): Promise<Octokit> {
    const token = await this.ghecAuthApi.getAccessToken();
    return new Octokit({ auth: token, baseUrl: this.ghecApiUrl });
  }

  async fetchAzureOnboardingPolicy(): Promise<AzureOnboardedApps> {
    const octokit = await this.getOctokit();
    const response = await octokit.repos.getContent({
      owner: GITHUB_ORG,
      repo: GITHUB_REPO,
      path: AZURE_POLICY_FILE_PATH,
      ref: 'main',
    });

    if (!('content' in response.data) || typeof response.data.content !== 'string') {
      throw new Error(`Invalid file content received from GitHub for ${AZURE_POLICY_FILE_PATH}`);
    }

    const content = Buffer.from(response.data.content, 'base64').toString('utf-8');
    let policyJson: AzureAppShortnamePolicy;

    try {
      policyJson = JSON.parse(content) as AzureAppShortnamePolicy;
    } catch {
      throw new Error(
        'Failed to parse Azure app shortname policy JSON from GaaS-Policy_as_Code. Please verify that the policy file contains valid JSON.',
      );
    }

    // Policy structure: properties.parameters.tagValue.defaultValue
    const rawTagValues = policyJson?.properties?.parameters?.tagValue?.defaultValue;
    const onboardedAppShortNames = Array.isArray(rawTagValues) ? rawTagValues : [];

    return { onboardedAppShortNames };
  }
}
